import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import PromotionCodeContainer from "app/pages/Booking/PromotionCode/PromotionCodeContainer";
import PaymentCouponsTitle from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponsTitle/PaymentCouponsTitle";
import CouponsCguPopoverButton from "app/pages/Booking/Payment/PaymentCoupons/CouponsCGUPopoverButton/CouponsCGUPopoverButton";
import PromotionCodeRadio from "app/pages/Booking/PromotionCode/PromotionCodeRadio";
import CreditRadio from "app/pages/Booking/Payment/PaymentCoupons/CreditRadio/CreditRadio";
import "./PaymentCoupons.scss";
import CreditNoteRadio from "app/pages/Booking/Payment/PaymentCoupons/CreditNoteRadio/CreditNoteRadio";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { USABLE_CREDITS_STATUS } from "app/constants";
import {
	sendTagOnCGUCreditClick,
	sendTagOnCGUCreditNoteClick,
	sendTagOnOnlyNonApplicableCouponMessageShow,
} from "app/utils/analytics";
import OnlyNonApplicableCouponMessage from "app/pages/Booking/Payment/PaymentCoupons/OnlyNonApplicableCouponMessage/OnlyNonApplicableCouponMessage";

const PaymentCoupons = ({
	enablePromotionCode,
	totalUsableCredit,
	totalUsableCreditNote,
	usableCredits,
	unusableCredits,
	usableCreditNotes,
	unusableCreditNotes,
	onlyNonApplicableCredits,
	onlyNonApplicableCreditNotes,
	field,
	onChange,
	onValidPromoCodeApplied,
	promotionValue,
	usableCouponsStatus,
	onPromoCodeFocus,
}) => {
	const hasCredits = totalUsableCredit > 0;
	const hasCreditNotes = totalUsableCreditNote > 0;
	const hasOnlyNonApplicableCredits =
		!hasCredits &&
		unusableCredits.length === onlyNonApplicableCredits.length &&
		onlyNonApplicableCredits.length > 0;

	const hasOnlyNonApplicableCreditNotes =
		!hasCreditNotes &&
		unusableCreditNotes.length === onlyNonApplicableCreditNotes.length &&
		onlyNonApplicableCreditNotes.length > 0;

	const promocodeComponent = hasCredits ? (
		<PromotionCodeRadio
			field={field}
			onChange={onChange}
			value={promotionValue}
			onValidPromoCodeApplied={onValidPromoCodeApplied}
			onPromoCodeFocus={onPromoCodeFocus}
		/>
	) : (
		<PromotionCodeContainer
			submit={false}
			showAmountInButton={true}
			onValidPromoCodeApplied={onValidPromoCodeApplied}
		/>
	);

	const isLoadingAllCoupons = usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;
	const isLoadingCreditNotes = usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES;

	const bookingTitle =
		hasCredits || hasOnlyNonApplicableCredits || isLoadingAllCoupons ? (
			<FormattedMessage id="booking.aside.coupons.title" />
		) : (
			<FormattedMessage id="booking.aside.promotion.code.title" />
		);

	const handleOnCreditCGUClick = useCallback(() => {
		sendTagOnCGUCreditClick();
	}, []);

	const handleOnCreditNoteCGUClick = useCallback(() => {
		sendTagOnCGUCreditNoteClick();
	}, []);

	const handleOnOnlyNonApplicableCreditMessageShow = useCallback(() => {
		sendTagOnOnlyNonApplicableCouponMessageShow("credit", onlyNonApplicableCredits);
	}, [onlyNonApplicableCredits]);

	const handleOnOnlyNonApplicableCreditNoteMessageShow = useCallback(() => {
		sendTagOnOnlyNonApplicableCouponMessageShow("refund", onlyNonApplicableCreditNotes);
	}, [onlyNonApplicableCreditNotes]);

	const shouldShowCreditPromoCodeSection =
		hasOnlyNonApplicableCredits || enablePromotionCode || hasCredits || isLoadingAllCoupons;

	return (
		(shouldShowCreditPromoCodeSection || hasCreditNotes) && (
			<div className="payment-credits">
				{shouldShowCreditPromoCodeSection && (
					<React.Fragment>
						<header className="payment__title">{bookingTitle}</header>
						<div className="payment__content">
							{hasOnlyNonApplicableCredits && (
								<OnlyNonApplicableCouponMessage
									title={
										<FormattedMessage id="payment.coupons.only.not.applicable.warning.title" />
									}
									message={
										<FormattedMessage id="payment.coupons.only.not.applicable.warning.message" />
									}
									coupons={onlyNonApplicableCredits}
									onShow={handleOnOnlyNonApplicableCreditMessageShow}
								/>
							)}

							{(isLoadingAllCoupons || enablePromotionCode || hasCredits) && (
								<div className="payment-credits__title">
									<PaymentCouponsTitle
										isPromocodeEnabled={enablePromotionCode}
										hasUsableCredits={isLoadingAllCoupons ? true : hasCredits}
									/>
									<CouponsCguPopoverButton
										isPromotionCodeEnabled={enablePromotionCode}
										hasCredits={isLoadingAllCoupons ? true : hasCredits}
										onClick={handleOnCreditCGUClick}
									/>
								</div>
							)}

							{isLoadingAllCoupons && (
								<div className="payment-credits__item">
									<LoaderBar />
								</div>
							)}
							{hasCredits && (
								<div className="payment-credits__item">
									<CreditRadio
										field={field}
										value={totalUsableCredit}
										usableCredits={usableCredits}
										unusableCredits={unusableCredits}
										onChange={onChange}
									/>
								</div>
							)}
							{enablePromotionCode && !isLoadingAllCoupons && (
								<div className="payment-credits__item">{promocodeComponent}</div>
							)}
						</div>
					</React.Fragment>
				)}

				{(hasOnlyNonApplicableCreditNotes || hasCreditNotes || isLoadingCreditNotes) && (
					<React.Fragment>
						<header className="payment__title">
							<FormattedMessage id="booking.aside.credit.note.title" />
						</header>
						<div className="payment__content">
							{hasOnlyNonApplicableCreditNotes ? (
								<OnlyNonApplicableCouponMessage
									title={
										<FormattedMessage id="payment.coupons.credit.note.only.not.applicable.warning.title" />
									}
									message={
										<FormattedMessage id="payment.coupons.credit.note.only.not.applicable.warning.message" />
									}
									coupons={onlyNonApplicableCreditNotes}
									onShow={handleOnOnlyNonApplicableCreditNoteMessageShow}
								/>
							) : (
								<div className="payment-credits__title">
									<FormattedMessage id="payment.coupons.title.creditnote" />
									<CouponsCguPopoverButton
										hasCreditNotes={true}
										onClick={handleOnCreditNoteCGUClick}
									/>
								</div>
							)}

							{isLoadingCreditNotes && (
								<div className="payment-credits__item">
									<LoaderBar />
								</div>
							)}

							{hasCreditNotes && (
								<div className="payment-credits__item">
									<CreditNoteRadio
										value={totalUsableCreditNote}
										usableCreditNotes={usableCreditNotes}
										unusableCreditNotes={unusableCreditNotes}
									/>
								</div>
							)}
						</div>
					</React.Fragment>
				)}
			</div>
		)
	);
};

PaymentCoupons.propTypes = {
	enablePromotionCode: PropTypes.bool,
	totalUsableCredit: PropTypes.number,
	totalUsableCreditNote: PropTypes.number,
	promotionValue: PropTypes.number,
	usableCredits: PropTypes.array,
	unusableCredits: PropTypes.array,
	usableCreditNotes: PropTypes.array,
	unusableCreditNotes: PropTypes.array,
	onlyNonApplicableCredits: PropTypes.array,
	onlyNonApplicableCreditNotes: PropTypes.array,
	field: PropTypes.object,
	usableCouponsStatus: PropTypes.string,
	onChange: PropTypes.func,
	onValidPromoCodeApplied: PropTypes.func,
	onPromoCodeFocus: PropTypes.func,
};

PaymentCoupons.defaultProps = {
	usableCredits: [],
	unusableCredits: [],
	usableCreditNotes: [],
	unusableCreditNotes: [],
	onlyNonApplicableCredits: [],
	onlyNonApplicableCreditNotes: [],
	field: {},
	onChange: () => {},
	onValidPromoCodeApplied: () => {},
	onPromoCodeFocus: () => {},
};

export default React.memo(PaymentCoupons);
